import { Node, Schema } from 'prosemirror-model';
import { EditorState } from 'prosemirror-state';

import { BlockquoteRange } from './types';

/**
 * 指定された選択範囲にあるすべてのblockquoteを検索する
 */
export function findBlockquoteRanges(
  state: EditorState,
  from: number,
  to: number,
): BlockquoteRange[] {
  const { schema } = state;
  const blockquoteRanges: BlockquoteRange[] = [];

  state.doc.nodesBetween(from, to, (node, pos) => {
    if (node.type === schema.nodes.blockquote) {
      blockquoteRanges.push({
        node,
        start: pos,
        end: pos + node.nodeSize,
      });
      return false;
    }
    return true;
  });

  return blockquoteRanges;
}

/**
 * ドキュメント内の指定されたインデックスまでの位置を計算する
 */
export function calculateNodePosition(parent: Node, targetIndex: number): number {
  let position = 0;
  for (let i = 0; i < targetIndex; i++) {
    position += parent.child(i).nodeSize;
  }
  return position;
}

/**
 * 前の兄弟blockquoteを検索する
 */
export function findPreviousBlockquote(
  schema: Schema,
  parent: Node,
  currentIndex: number,
): { node: Node; pos: number } | null {
  if (currentIndex === 0) {
    return null;
  }

  const prevSibling = parent.child(currentIndex - 1);
  if (prevSibling.type !== schema.nodes.blockquote) {
    return null;
  }

  const position = calculateNodePosition(parent, currentIndex - 1);
  return { node: prevSibling, pos: position };
}

/**
 * 後の兄弟blockquoteを検索する
 */
export function findNextBlockquote(
  schema: Schema,
  parent: Node,
  currentIndex: number,
): { node: Node; pos: number } | null {
  if (currentIndex >= parent.childCount - 1) {
    return null;
  }

  const nextSibling = parent.child(currentIndex + 1);
  if (nextSibling.type !== schema.nodes.blockquote) {
    return null;
  }

  const position = calculateNodePosition(parent, currentIndex + 1);
  return { node: nextSibling, pos: position };
}

/**
 * 指定された位置の前後にある隣接するblockquoteを検出する
 */
export function findAdjacentBlockquotes(
  state: EditorState,
  from: number,
  to: number,
): { before: { node: Node; pos: number } | null; after: { node: Node; pos: number } | null } {
  const { schema, doc } = state;

  try {
    const $from = doc.resolve(from);
    const $to = doc.resolve(to);

    // ドキュメント深度が不適切な場合は早期リターン
    const documentDepth = 0;
    if ($from.depth <= documentDepth || $to.depth <= documentDepth) {
      return { before: null, after: null };
    }

    // ドキュメントルートレベルでの兄弟ノードを検索
    const fromIndex = $from.index(documentDepth);
    const toIndex = $to.index(documentDepth);
    const documentRoot = $from.node(documentDepth);

    const beforeBlockquote = findPreviousBlockquote(schema, documentRoot, fromIndex);
    const afterBlockquote = findNextBlockquote(schema, documentRoot, toIndex);

    return { before: beforeBlockquote, after: afterBlockquote };
  } catch {
    return { before: null, after: null };
  }
}
